const VISITOR_KEY = "_gep_vid";
const SESSION_KEY = "_gep_sid";
const LAST_ACTIVE_KEY = "_gep_last_active";
const FIRST_SEEN_KEY = "_gep_first_seen";
const CONSENT_KEY = "_gep_consent";
const CONSENT_REVOKED_KEY = "_gep_consent_revoked_at";
const MERCHANT_KEY = "_gep_merchant";
const MERCHANT_CHANGED_KEY = "_gep_merchant_changed_at";

export const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const VISITOR_MAX_AGE_MS = 2 * 365 * 24 * 60 * 60 * 1000;

// Authoritative in-memory consent. Set only when a consent write could not be
// persisted, so a stale localStorage entry cannot override a revocation.
let consentOverride: boolean | undefined;

type StorageLike = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
};

function storage(): StorageLike | undefined {
  try {
    if (typeof localStorage !== "undefined") return localStorage;
  } catch {
    return undefined;
  }
  return undefined;
}

function cookie(): string {
  try {
    return typeof document !== "undefined" ? document.cookie : "";
  } catch {
    return "";
  }
}

function readCookie(name: string): string {
  const parts = cookie().split(";");
  for (const part of parts) {
    const trimmed = part.trim();
    if (trimmed.startsWith(`${name}=`)) {
      return decodeURIComponent(trimmed.slice(name.length + 1));
    }
  }
  return "";
}

function writeCookie(name: string, value: string, maxAgeMs: number) {
  try {
    if (typeof document === "undefined") return;
    document.cookie = `${name}=${encodeURIComponent(
      value,
    )}; path=/; max-age=${Math.floor(maxAgeMs / 1000)}; SameSite=Lax`;
  } catch {
    // ignore
  }
}

export function randomId(): string {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch {
    // ignore
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export function readValue(key: string): string {
  try {
    const fromStore = storage()?.getItem(key);
    if (fromStore) return fromStore;
  } catch {
    // A blocked storage read must not break the tracker; fall back to the
    // cookie below.
  }
  return readCookie(key);
}

/**
 * Persists `key=value` to localStorage and the cookie. Returns whether the
 * localStorage write succeeded; the cookie is always attempted as a fallback.
 */
export function writeValue(
  key: string,
  value: string,
  maxAgeMs: number,
): boolean {
  const store = storage();
  if (store) {
    try {
      store.setItem(key, value);
      writeCookie(key, value, maxAgeMs);
      return true;
    } catch {
      // localStorage can be full or blocked. Drop any stale value so the cookie
      // written below (the fresh value) is not shadowed by an old localStorage
      // entry on the next read - e.g. a failed consent revocation must not leave
      // `_gep_consent=true` behind.
      try {
        store.removeItem(key);
      } catch {
        // ignore: storage fully unavailable, nothing else we can do
      }
    }
  }
  writeCookie(key, value, maxAgeMs);
  return false;
}

export function getVisitorId(): string {
  const existing = readValue(VISITOR_KEY);
  if (existing) {
    writeValue(VISITOR_KEY, existing, VISITOR_MAX_AGE_MS);
    return existing;
  }
  const visitorId = `g-${randomId()}`;
  writeValue(VISITOR_KEY, visitorId, VISITOR_MAX_AGE_MS);
  return visitorId;
}

export function getSessionId(now: number = Date.now()): string {
  const visitorId = getVisitorId();
  const existing = readValue(SESSION_KEY);
  const lastActive = Number(readValue(LAST_ACTIVE_KEY) || 0);
  const valid =
    existing &&
    existing.startsWith(`${visitorId}.`) &&
    lastActive > 0 &&
    now - lastActive <= SESSION_TIMEOUT_MS;
  let sessionId = existing;
  if (!valid) {
    sessionId = `${visitorId}.${Math.floor(now / 1000)}`;
    // GA4 parity uses the session start second, but rotating twice within the
    // same second (or rebuilding a session created in that same second) would
    // otherwise reuse an id. Add a random suffix only on that collision.
    if (sessionId === existing) {
      sessionId = `${sessionId}.${randomId()}`;
    }
  }
  writeValue(SESSION_KEY, sessionId, VISITOR_MAX_AGE_MS);
  touchSession(now);
  return sessionId;
}

export function touchSession(now: number = Date.now()) {
  writeValue(LAST_ACTIVE_KEY, String(now), VISITOR_MAX_AGE_MS);
}

export function isFirstVisit(): boolean {
  const seen = readValue(FIRST_SEEN_KEY);
  if (seen) return false;
  writeValue(FIRST_SEEN_KEY, String(Date.now()), VISITOR_MAX_AGE_MS);
  return true;
}

export function getConsent(): boolean {
  if (consentOverride !== undefined) return consentOverride;
  // The cookie is authoritative for consent: it is written even when a
  // localStorage mutation fails (full/blocked), so a stale `_gep_consent=true`
  // entry cannot reverse a revocation after a reload.
  const fromCookie = readCookie(CONSENT_KEY);
  if (fromCookie) return fromCookie !== "false";
  return readValue(CONSENT_KEY) !== "false";
}

export function setStoredConsent(granted: boolean) {
  const persisted = writeValue(
    CONSENT_KEY,
    granted ? "true" : "false",
    VISITOR_MAX_AGE_MS,
  );
  // If the durable store rejected the write, keep the value authoritative in
  // memory so a stale localStorage entry cannot override a consent change.
  consentOverride = persisted ? undefined : granted;
  if (!granted) {
    // Record when consent was revoked. The cookie is written even if the
    // localStorage mutation fails, so a later load can discard events queued
    // before the revocation even when the queue purge could not be persisted.
    writeValue(
      CONSENT_REVOKED_KEY,
      new Date().toISOString(),
      VISITOR_MAX_AGE_MS,
    );
  }
}

export function getConsentRevokedAt(): string {
  const fromCookie = readCookie(CONSENT_REVOKED_KEY);
  if (fromCookie) return fromCookie;
  return readValue(CONSENT_REVOKED_KEY);
}

export function resetConsentOverride(): void {
  consentOverride = undefined;
}

export function getStoredMerchant(): string {
  return readValue(MERCHANT_KEY);
}

export function setStoredMerchant(merchantId: string): void {
  writeValue(MERCHANT_KEY, merchantId, VISITOR_MAX_AGE_MS);
}

export function setStoredMerchantChangedAt(iso: string): void {
  writeValue(MERCHANT_CHANGED_KEY, iso, VISITOR_MAX_AGE_MS);
}

export function getMerchantChangedAt(): string {
  const fromCookie = readCookie(MERCHANT_CHANGED_KEY);
  if (fromCookie) return fromCookie;
  return readValue(MERCHANT_CHANGED_KEY);
}

export function clearIdentity() {
  storage()?.removeItem(SESSION_KEY);
  storage()?.removeItem(LAST_ACTIVE_KEY);
}
